/**
 * The shell's UI language as every HTTP client and the WebSocket send it to
 * the backend, which renders its error messages in that language. Read at
 * request time, so a language switch applies to the next request at once.
 */

/** The UI languages of the shell — BCP 47 language tags. */
export type ShellLocale = 'de' | 'en';

/**
 * Where the shell keeps its UI language for the HTTP clients. Its source is
 * the `locale` account config: the shell's settings (connect `settings.ts`)
 * mirror the effective value here in a synchronous watch — at boot from the
 * config's boot cache, then on every change (setShellLocale, a reload of the
 * configs) — so a request sent right after a switch already carries the new
 * language. Nothing else writes it.
 */
export const SHELL_LOCALE_STORAGE_KEY = 'formica.connect.locale';

const SHELL_LOCALES: readonly ShellLocale[] = ['de', 'en'];
const FALLBACK_LOCALE: ShellLocale = 'de';

/** The shell's UI language; 'de' when it is unset, invalid or storage throws. */
export function readShellLocale(): ShellLocale {
  try {
    const stored = globalThis.localStorage?.getItem(SHELL_LOCALE_STORAGE_KEY);
    if (stored && (SHELL_LOCALES as readonly string[]).includes(stored)) return stored as ShellLocale;
  } catch {
    /* storage blocked: fall back */
  }
  return FALLBACK_LOCALE;
}

/** The Accept-Language header with the shell's current UI language. */
export function localeHeader(): { 'Accept-Language': ShellLocale } {
  return { 'Accept-Language': readShellLocale() };
}

/**
 * Returns url with the query parameter lang=<locale> (the shell's current UI
 * language when locale is omitted) — for the WebSocket, whose handshake a
 * browser cannot give headers. An existing lang parameter is replaced; every
 * other part of the URL (other parameters, fragment, relative or absolute
 * form) stays as it is.
 */
export function withLocaleQuery(url: string, locale: ShellLocale = readShellLocale()): string {
  const hashAt = url.indexOf('#');
  const hash = hashAt >= 0 ? url.slice(hashAt) : '';
  const beforeHash = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const queryAt = beforeHash.indexOf('?');
  const path = queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash;
  const query = queryAt >= 0 ? beforeHash.slice(queryAt + 1) : '';
  const pairs = query.split('&').filter((pair) => pair !== '' && queryKey(pair) !== 'lang');
  pairs.push(`lang=${encodeURIComponent(locale)}`);
  return `${path}?${pairs.join('&')}${hash}`;
}

function queryKey(pair: string): string {
  const raw = pair.split('=', 1)[0].replace(/\+/g, ' ');
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
